import { createSlice, PayloadAction } from "@reduxjs/toolkit";

import {
  fetchAdminLeaves,
  fetchAdminLeavesCompanies,
  fetchAdminLeavesEmployees,
  fetchAdminLeaveTypes,
  updateAdminLeaveStatus,
} from "./thunks";

import type {
  AdminLeavesActiveCompany,
  AdminLeavesFilters,
  AdminLeavesState,
  CompanyOption,
  EmployeeOption,
  LeaveTypeOption,
} from "./types";

export const INITIAL_ADMIN_LEAVES_FILTERS: AdminLeavesFilters = {
  search: "",
  employeeId: null,
  leaveTypeId: null,
  status: null,
  startDate: null,
  endDate: null,
  page: 1,
  limit: 25,
};

const DEFAULT_ACTIVE_COMPANY: AdminLeavesActiveCompany = {
  companyId: null,
  companyType: null,
  companyName: null,
};

const DEFAULT_PAGINATION = {
  total: 0,
  current_page: 1,
  per_page: 25,
  total_pages: 0,
};

const initialState: AdminLeavesState = {
  list: [],
  stats: null,
  pagination: DEFAULT_PAGINATION,
  filters: INITIAL_ADMIN_LEAVES_FILTERS,
  activeCompany: DEFAULT_ACTIVE_COMPANY,
  companies: [],
  employees: [],
  leaveTypes: [],
  loading: false,
  companiesLoading: false,
  employeesLoading: false,
  leaveTypesLoading: false,
  error: null,
};

const adminLeavesSlice = createSlice({
  name: "adminLeaves",
  initialState,
  reducers: {
    setAdminLeavesActiveCompany: (
      state,
      action: PayloadAction<AdminLeavesActiveCompany>,
    ) => {
      state.activeCompany = action.payload;
      state.employees = [];
      state.leaveTypes = [];
      state.filters = { ...INITIAL_ADMIN_LEAVES_FILTERS };
    },
    setAdminLeavesFilters: (
      state,
      action: PayloadAction<Partial<AdminLeavesFilters>>,
    ) => {
      state.filters = { ...state.filters, ...action.payload };
    },
    resetAdminLeavesFilters: (state) => {
      state.filters = { ...INITIAL_ADMIN_LEAVES_FILTERS };
    },
    clearAdminLeavesError: (state) => {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchAdminLeaves.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchAdminLeaves.fulfilled, (state, action) => {
        state.loading = false;
        const payload = action.payload || {};
        state.list = Array.isArray(payload.leaveHistory)
          ? payload.leaveHistory
          : [];
        state.stats = payload.stats
          ? {
              totalApplications: payload.stats.totalApplications ?? 0,
              planned: payload.stats.planned ?? 0,
              unlimited: payload.stats.unlimited ?? 0,
              pending: payload.stats.pending ?? 0,
              approved: payload.stats.approved ?? 0,
              rejected: payload.stats.rejected ?? 0,
              cancelled: payload.stats.cancelled ?? 0,
            }
          : null;
        if (payload.pagination) {
          state.pagination = {
            total: payload.pagination.total ?? 0,
            current_page: payload.pagination.current_page ?? state.filters.page,
            per_page: payload.pagination.per_page ?? state.filters.limit,
            total_pages: payload.pagination.total_pages ?? 0,
          };
        } else {
          state.pagination = {
            ...DEFAULT_PAGINATION,
            total: state.list.length,
            current_page: state.filters.page,
            per_page: state.filters.limit,
            total_pages: Math.max(
              1,
              Math.ceil(state.list.length / state.filters.limit),
            ),
          };
        }
      })
      .addCase(fetchAdminLeaves.rejected, (state, action) => {
        state.loading = false;
        state.error = (action.payload as string) || "Failed to load leaves";
      })

      .addCase(fetchAdminLeavesCompanies.pending, (state) => {
        state.companiesLoading = true;
      })
      .addCase(fetchAdminLeavesCompanies.fulfilled, (state, action) => {
        state.companiesLoading = false;
        const list = Array.isArray(action.payload) ? action.payload : [];
        const mapped: CompanyOption[] = list.map((c: any) => ({
          value: `${c.id}|${c.type}`,
          label: `${c.name} (${c.type})`,
          companyId: c.id,
          companyType: c.type,
        }));
        state.companies = mapped;
      })
      .addCase(fetchAdminLeavesCompanies.rejected, (state, action) => {
        state.companiesLoading = false;
        state.error = (action.payload as string) || "Failed to load companies";
      })

      .addCase(fetchAdminLeavesEmployees.pending, (state) => {
        state.employeesLoading = true;
      })
      .addCase(fetchAdminLeavesEmployees.fulfilled, (state, action) => {
        state.employeesLoading = false;
        const list = Array.isArray(action.payload) ? action.payload : [];
        const mapped: EmployeeOption[] = list.map((emp: any) => ({
          value: emp.id.toString(),
          label:
            `${emp.salutation || ""} ${emp.fname || ""} ${emp.lname || ""}`.trim(),
        }));
        state.employees = mapped;
      })
      .addCase(fetchAdminLeavesEmployees.rejected, (state, action) => {
        state.employeesLoading = false;
        state.error = (action.payload as string) || "Failed to load employees";
      })

      .addCase(fetchAdminLeaveTypes.pending, (state) => {
        state.leaveTypesLoading = true;
      })
      .addCase(fetchAdminLeaveTypes.fulfilled, (state, action) => {
        state.leaveTypesLoading = false;
        const list = Array.isArray(action.payload) ? action.payload : [];
        const mapped: LeaveTypeOption[] = list.map((t: any) => ({
          value: t.id.toString(),
          label: t.name,
          unlimited: Boolean(t.unlimited),
          companyType: t.companyType,
        }));
        state.leaveTypes = mapped;
      })
      .addCase(fetchAdminLeaveTypes.rejected, (state, action) => {
        state.leaveTypesLoading = false;
        state.error =
          (action.payload as string) || "Failed to load leave types";
      })

      .addCase(updateAdminLeaveStatus.rejected, (state, action) => {
        state.error =
          (action.payload as string) || "Failed to update leave status";
      });
  },
});

export const {
  setAdminLeavesActiveCompany,
  setAdminLeavesFilters,
  resetAdminLeavesFilters,
  clearAdminLeavesError,
} = adminLeavesSlice.actions;

export default adminLeavesSlice.reducer;
